import { Link } from 'react-router-dom';
import { useAuPouce } from '../partage/surface';
import { motsDuDossier } from '../partage/vocabulaire';
import { OuvrirUnDossier, PartirDUnModele } from '../maintenant';
import './ProjetsVides.css';

/**
 * « Projets », sans projet à soi : la page présente « Maintenant » (ticket 14 de
 * retours-testeurs, option B de l'ancienne maquette des pages vides,
 * décisions d'Igor du 2026-09-27).
 *
 * Un nouvel utilisateur arrive sur « Maintenant » (`Premier.tsx`), qui présente les projets.
 * S'il vient ici, la page lui dit ce que « Maintenant » fera de ses projets, avec un exemple,
 * lui montre ce qu'il verra ici, et le laisse ouvrir son premier dossier sur place. Chaque
 * page vide présente l'autre.
 *
 * L'aperçu et les lignes d'exemple sont des images, pas des boutons : marqués « Exemple »,
 * pâles, rien ne s'y clique.
 */

const IDEES = ['Déménagement', 'Recruter quelqu’un', 'Changer de voiture'];

const CE_QU_IL_FAIT = [
  {
    titre: 'Il choisit pour toi',
    corps:
      'Tu dis combien de temps tu as et où tu es. Il te propose trois choses faisables, là, tout de suite, tous projets confondus.',
  },
  {
    titre: 'Il cache ce qui attend',
    corps:
      'Ce qui attend quelqu’un d’autre, ou une étape pas encore faite, ne te tombe pas sous les yeux. Rien d’impossible dans la liste.',
  },
  {
    titre: 'Il te dit ce qui s’ouvre',
    corps:
      'Quand tu coches « C’est fait », il te montre ce que ça vient de débloquer, et ce qui presse avant une échéance.',
  },
];

/**
 * Trois dossiers inventés, dans les colonnes de la vraie liste : une forme, un acquis, une
 * date, ce qu'il s'y passe.
 */
const LIGNES_D_EXEMPLE = [
  {
    nom: 'Préparer un voyage',
    forme: '4 étapes',
    acquis: '3 sur 17 acquis',
    tot: 'Au plus tôt le 14 octobre',
    passe: 'Attend le gîte',
    monde: true,
  },
  {
    nom: 'Déménagement',
    forme: '6 étapes',
    acquis: '0 sur 32 acquis',
    tot: 'Au plus tôt le 2 novembre',
    passe: 'À toi de jouer',
    monde: false,
  },
  {
    nom: 'Changer de voiture',
    forme: '3 étapes',
    acquis: '5 sur 9 acquis',
    tot: 'Au plus tôt demain',
    passe: 'Rendez-vous samedi',
    monde: false,
  },
];

export function ProjetsVides({ onOuvrir, refus = null }: { onOuvrir: (titre: string) => void; refus?: string | null }) {
  const m = motsDuDossier(useAuPouce());
  return (
    <div className="vides">
      <div className="vides-centre">
        <h1>Tes projets, rangés pour toi</h1>
        <p className="vides-amorce">
          {m.iciViendront} Tu n’as pas à les parcourir chaque jour&nbsp;: c’est
          «&nbsp;<strong>Maintenant</strong>&nbsp;» qui les lit pour toi et te dit quoi faire.
        </p>

        <div className="vides-deux">
          <section className="vides-fait">
            <h2>Ce que «&nbsp;Maintenant&nbsp;» fait de tes projets</h2>
            <p className="vides-sous">Trois choses que tu n’as plus à faire toi-même.</p>
            <ol className="vides-points">
              {CE_QU_IL_FAIT.map((point, i) => (
                <li key={point.titre}>
                  <div className="vides-point-tete">
                    <span className="premier-numero" aria-hidden="true">
                      {i + 1}
                    </span>
                    <h3>{point.titre}</h3>
                  </div>
                  <p>{point.corps}</p>
                </li>
              ))}
            </ol>
          </section>

          <ApercuDeMaintenant />
        </div>

        <section className="vides-exemples" aria-label={m.exemplesDe}>
          <h2>
            Ce que tu verras ici <span className="vides-exemple vides-exemple--pose">Exemples</span>
          </h2>
          <div className="vides-lignes" data-tuto="dossiers-exemples">
            {LIGNES_D_EXEMPLE.map((ligne) => (
              <div className="vides-ligne" key={ligne.nom}>
                <span className="vides-ligne-nom">{ligne.nom}</span>
                <span>{ligne.forme}</span>
                <span>{ligne.acquis}</span>
                <span>{ligne.tot}</span>
                <span className={ligne.monde ? 'vides-ligne-monde' : undefined}>{ligne.passe}</span>
              </div>
            ))}
          </div>
          <p className="vides-legende">
            {m.uneLignePar}&nbsp;: où il en est, quand il peut finir au plus tôt, ce qu’il s’y
            passe.
          </p>
        </section>

        <div className="vides-premier">
          <OuvrirUnDossier
            libelle={m.tonPremier}
            exemple="Préparer un voyage"
            idees={IDEES}
            onOuvrir={onOuvrir}
            refus={refus}
          >
            <p className="vides-aussi">
              Tu peux aussi le créer depuis <Link to="/maintenant">«&nbsp;Maintenant&nbsp;»</Link>,
              c’est pareil.
            </p>
          </OuvrirUnDossier>
        </div>

        <PartirDUnModele />
      </div>
    </div>
  );
}

/** Une page « Maintenant » sur un voyage, dessinée pour l'exemple : rien ne s'y clique. */
function ApercuDeMaintenant() {
  const m = motsDuDossier(useAuPouce());
  return (
    <figure className="vides-apercu" aria-label="Exemple de la page « Maintenant »">
      <span className="vides-exemple">Exemple</span>
      <div className="vides-apercu-question">Maintenant · j’ai 20 minutes, chez moi</div>
      <div className="vides-apercu-filtres" aria-hidden="true">
        <span>20 min ▾</span>
        <span>Chez moi ▾</span>
        <span>{m.tousLes} ▾</span>
      </div>
      <ol className="vides-apercu-taches">
        <li>
          <b aria-hidden="true">1</b>
          <div>
            <div className="vides-apercu-titre">Demander ses disponibilités au gîte</div>
            <div className="vides-apercu-mention">
              Préparer un voyage · débloque «&nbsp;Verser l’acompte&nbsp;»
            </div>
          </div>
        </li>
        <li>
          <b aria-hidden="true">2</b>
          <div>
            <div className="vides-apercu-titre">Fixer le budget du voyage</div>
            <div className="vides-apercu-mention">Préparer un voyage · une heure</div>
          </div>
        </li>
        <li>
          <b aria-hidden="true">3</b>
          <div>
            <div className="vides-apercu-titre">Vérifier les cartes d’identité</div>
            <div className="vides-apercu-mention">Avant de partir · dix minutes</div>
          </div>
        </li>
      </ol>
      <div className="vides-apercu-presse">
        «&nbsp;Poser les congés&nbsp;»&nbsp;: à faire avant vendredi.
      </div>
    </figure>
  );
}
